import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import {
  ENG_FILES_BUCKET,
  categoryForFileName,
  safeStorageName,
  type EngCostCategory,
  type EngFileCategory,
  type EngItemRelationship,
  type EngMemberRole,
  type EngPrintStatus,
  type EngStatus,
} from './model';

/**
 * Data hooks for the Engineering section (migration 142).
 *
 * Read model: anyone with the Engineering section sees every project in
 * the org. Write model: the project team (owner, creator, members) plus
 * admin / super_admin; RLS enforces both, these hooks only shape the
 * requests. Files, folders and versions are rejected server-side while a
 * project is in a locked status (design_locked / production).
 *
 * Storage: private `eng-files` bucket. Versions live at
 *   <org>/<project>/<file_id>/<stamp>-<filename>
 * and print photos at
 *   <org>/<project>/prints/<print_id>/<stamp>-<filename>
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LiteProfile {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url: string | null;
}

export interface EngProject {
  id: string;
  organization_id: string;
  code: string;
  name: string;
  description: string | null;
  status: EngStatus;
  owner_id: string | null;
  pm_project_id: string | null;
  start_date: string | null;
  target_date: string | null;
  budget_cents: number | null;
  design_locked_at: string | null;
  design_locked_by: string | null;
  image_path: string | null;
  created_by: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  owner: LiteProfile | null;
  /** Null when the PM project is missing or the caller isn't a member of it. */
  pm_project: { id: string; name: string; archived_at: string | null } | null;
  eng_project_members: Array<{
    profile_id: string;
    role: EngMemberRole;
    profiles: LiteProfile | null;
  }>;
}

export interface EngProjectListRow extends EngProject {
  fileCount: number;
  printCount: number;
  /** Logged costs + 3D print material costs. */
  spentCents: number;
}

export interface EngFolder {
  id: string;
  organization_id: string;
  project_id: string;
  parent_id: string | null;
  name: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EngFileVersion {
  id: string;
  organization_id: string;
  project_id: string;
  file_id: string;
  version_no: number;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  change_summary: string | null;
  uploaded_by: string | null;
  created_at: string;
  uploader?: LiteProfile | null;
}

export interface EngFile {
  id: string;
  organization_id: string;
  project_id: string;
  folder_id: string | null;
  name: string;
  description: string | null;
  category: EngFileCategory;
  item_id: string | null;
  current_version_id: string | null;
  version_count: number;
  checked_out_by: string | null;
  checked_out_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  current_version: Omit<EngFileVersion, 'uploader' | 'organization_id' | 'project_id' | 'file_id'> | null;
  checked_out_profile: LiteProfile | null;
  item: { id: string; sku: string; name: string } | null;
}

export interface EngPrint {
  id: string;
  organization_id: string;
  project_id: string;
  file_id: string | null;
  file_version_id: string | null;
  title: string;
  equipment_id: string | null;
  printer_name: string | null;
  material: string | null;
  color: string | null;
  quantity: number;
  layer_height_mm: number | null;
  infill_percent: number | null;
  print_time_minutes: number | null;
  material_grams: number | null;
  cost_cents: number | null;
  status: EngPrintStatus;
  printed_on: string | null;
  printed_by: string | null;
  notes: string | null;
  changes_needed: string | null;
  photo_path: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  file: { id: string; name: string } | null;
  version: { id: string; version_no: number; storage_path: string; file_name: string } | null;
  printer: { id: string; name: string; asset_tag: string } | null;
  printed_by_profile: LiteProfile | null;
  project: { id: string; code: string; name: string } | null;
}

export interface EngCost {
  id: string;
  organization_id: string;
  project_id: string;
  description: string;
  category: EngCostCategory;
  vendor: string | null;
  reference: string | null;
  item_id: string | null;
  print_id: string | null;
  quantity: number | null;
  unit_cents: number | null;
  amount_cents: number;
  incurred_on: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  item: { id: string; sku: string; name: string } | null;
  print: { id: string; title: string } | null;
  creator: LiteProfile | null;
}

export interface EngProjectItem {
  project_id: string;
  item_id: string;
  organization_id: string;
  relationship: EngItemRelationship;
  note: string | null;
  added_by: string | null;
  created_at: string;
  item: {
    id: string;
    sku: string;
    name: string;
    kind: string;
    status: string;
    image_path: string | null;
  } | null;
}

/** eng_project_items row seen from the catalog item's side. */
export interface EngProjectLinkForItem {
  project_id: string;
  item_id: string;
  relationship: EngItemRelationship;
  note: string | null;
  created_at: string;
  project: {
    id: string;
    code: string;
    name: string;
    status: EngStatus;
    archived_at: string | null;
    updated_at: string;
    design_locked_at: string | null;
  } | null;
}

export interface EngNote {
  id: string;
  organization_id: string;
  project_id: string;
  author_id: string | null;
  body: string;
  created_at: string;
  updated_at: string;
  author: LiteProfile | null;
}

export interface EngStatusHistoryRow {
  id: string;
  project_id: string;
  from_status: EngStatus | null;
  to_status: EngStatus;
  changed_by: string | null;
  note: string | null;
  created_at: string;
  actor: LiteProfile | null;
}

/** Whether the caller can see the Engineering section (role + per-person grant). */
export function useCanSeeEngineering(): boolean {
  const { role, profile } = useAuth();
  return canRead(role, 'engineering', profile?.section_access);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const engKeys = {
  all: ['engineering'] as const,
  projects: (includeArchived: boolean) => [...engKeys.all, 'projects', { includeArchived }] as const,
  project: (id: string) => [...engKeys.all, 'project', id] as const,
  folders: (projectId: string) => [...engKeys.all, projectId, 'folders'] as const,
  files: (projectId: string) => [...engKeys.all, projectId, 'files'] as const,
  versions: (fileId: string) => [...engKeys.all, 'file', fileId, 'versions'] as const,
  prints: (projectId: string | null) => [...engKeys.all, 'prints', projectId ?? 'all'] as const,
  costs: (projectId: string) => [...engKeys.all, projectId, 'costs'] as const,
  items: (projectId: string) => [...engKeys.all, projectId, 'items'] as const,
  itemLinks: (itemId: string) => [...engKeys.all, 'item', itemId] as const,
  notes: (projectId: string) => [...engKeys.all, projectId, 'notes'] as const,
  history: (projectId: string) => [...engKeys.all, projectId, 'history'] as const,
  signedUrl: (path: string, download: string | null) => [...engKeys.all, 'url', path, download] as const,
};

// ---------------------------------------------------------------------------
// Select fragments
// ---------------------------------------------------------------------------

const PROFILE_FIELDS = 'id, full_name, email, avatar_url';

const PROJECT_SELECT = `*, owner:profiles!eng_projects_owner_id_fkey(${PROFILE_FIELDS}), pm_project:projects!eng_projects_pm_project_id_fkey(id, name, archived_at), eng_project_members(profile_id, role, profiles!eng_project_members_profile_id_fkey(${PROFILE_FIELDS}))`;

const PROJECT_LIST_SELECT = `${PROJECT_SELECT}, eng_files(id), eng_prints(id, cost_cents), eng_costs(amount_cents)`;

const VERSION_FIELDS =
  'id, version_no, storage_path, file_name, mime_type, size_bytes, change_summary, uploaded_by, created_at';

const FILE_SELECT = `*, current_version:eng_file_versions!eng_files_current_version_fkey(${VERSION_FIELDS}), checked_out_profile:profiles!eng_files_checked_out_by_fkey(${PROFILE_FIELDS}), item:items!eng_files_item_id_fkey(id, sku, name)`;

const VERSION_SELECT = `*, uploader:profiles!eng_file_versions_uploaded_by_fkey(${PROFILE_FIELDS})`;

const PRINT_SELECT = `*, file:eng_files!eng_prints_file_id_fkey(id, name), version:eng_file_versions!eng_prints_file_version_id_fkey(id, version_no, storage_path, file_name), printer:equipment!eng_prints_equipment_id_fkey(id, name, asset_tag), printed_by_profile:profiles!eng_prints_printed_by_fkey(${PROFILE_FIELDS}), project:eng_projects!eng_prints_project_id_fkey(id, code, name)`;

const COST_SELECT = `*, item:items!eng_costs_item_id_fkey(id, sku, name), print:eng_prints!eng_costs_print_id_fkey(id, title), creator:profiles!eng_costs_created_by_fkey(${PROFILE_FIELDS})`;

const PROJECT_ITEM_SELECT = `*, item:items!eng_project_items_item_id_fkey(id, sku, name, kind, status, image_path)`;

const ITEM_LINK_SELECT = `project_id, item_id, relationship, note, created_at, project:eng_projects!eng_project_items_project_id_fkey(id, code, name, status, archived_at, updated_at, design_locked_at)`;

const NOTE_SELECT = `*, author:profiles!eng_notes_author_id_fkey(${PROFILE_FIELDS})`;

const HISTORY_SELECT = `*, actor:profiles!eng_status_history_changed_by_fkey(${PROFILE_FIELDS})`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function isUniqueViolation(error: unknown): boolean {
  return (error as { code?: string } | null)?.code === '23505';
}

function stamp(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function versionPath(orgId: string, projectId: string, fileId: string, filename: string): string {
  return `${orgId}/${projectId}/${fileId}/${stamp()}-${safeStorageName(filename)}`;
}

function printPhotoPath(orgId: string, projectId: string, printId: string, filename: string): string {
  return `${orgId}/${projectId}/prints/${printId}/${stamp()}-${safeStorageName(filename)}`;
}

/** Download a signed URL through a hidden anchor so the browser saves it. */
export function triggerBrowserDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Signed URL for a storage object; `download` forces an attachment with that filename. */
export async function createEngSignedUrl(path: string, download?: string | null): Promise<string> {
  const { data, error } = await supabase.storage
    .from(ENG_FILES_BUCKET)
    .createSignedUrl(path, 60 * 10, download ? { download } : undefined);
  if (error) throw error;
  if (!data?.signedUrl) throw new Error('Could not create a download link.');
  return data.signedUrl;
}

function normalizePrint(row: EngPrint): EngPrint {
  return {
    ...row,
    layer_height_mm: row.layer_height_mm == null ? null : Number(row.layer_height_mm),
    material_grams: row.material_grams == null ? null : Number(row.material_grams),
  };
}

function normalizeCost(row: EngCost): EngCost {
  return { ...row, quantity: row.quantity == null ? null : Number(row.quantity) };
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

type RawProjectListRow = EngProject & {
  eng_files?: Array<{ id: string }> | null;
  eng_prints?: Array<{ id: string; cost_cents: number | null }> | null;
  eng_costs?: Array<{ amount_cents: number }> | null;
};

export function useEngProjects(params?: { includeArchived?: boolean; enabled?: boolean }) {
  const { profile } = useAuth();
  const includeArchived = params?.includeArchived ?? false;
  return useQuery({
    queryKey: engKeys.projects(includeArchived),
    enabled: !!profile && params?.enabled !== false,
    queryFn: async () => {
      let q = supabase
        .from('eng_projects' as never)
        .select(PROJECT_LIST_SELECT)
        .order('updated_at', { ascending: false });
      if (!includeArchived) q = q.is('archived_at', null);
      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as RawProjectListRow[]).map((raw) => {
        const { eng_files, eng_prints, eng_costs, ...rest } = raw;
        const printCosts = (eng_prints ?? []).reduce((sum, p) => sum + (p.cost_cents ?? 0), 0);
        const logged = (eng_costs ?? []).reduce((sum, c) => sum + (c.amount_cents ?? 0), 0);
        return {
          ...(rest as EngProject),
          eng_project_members: rest.eng_project_members ?? [],
          fileCount: (eng_files ?? []).length,
          printCount: (eng_prints ?? []).length,
          spentCents: printCosts + logged,
        } satisfies EngProjectListRow;
      });
    },
  });
}

export function useEngProject(id: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: engKeys.project(id ?? 'noop'),
    enabled: !!profile && !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_projects' as never)
        .select(PROJECT_SELECT)
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as EngProject;
      return { ...row, eng_project_members: row.eng_project_members ?? [] };
    },
  });
}

export interface CreateEngProjectInput {
  code: string;
  name: string;
  description?: string | null;
  status?: EngStatus;
  owner_id?: string | null;
  start_date?: string | null;
  target_date?: string | null;
  budget_cents?: number | null;
  pm_project_id?: string | null;
  memberProfileIds?: string[];
}

export function useCreateEngProject() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateEngProjectInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const code = input.code.trim();
      const name = input.name.trim();
      if (!code) throw new Error('Project code is required.');
      if (!name) throw new Error('Project name is required.');

      const { data, error } = await supabase
        .from('eng_projects' as never)
        .insert({
          organization_id: profile.organization_id,
          created_by: profile.id,
          code,
          name,
          description: blankToNull(input.description),
          status: input.status ?? 'concept',
          owner_id: input.owner_id ?? profile.id,
          start_date: input.start_date || null,
          target_date: input.target_date || null,
          budget_cents: input.budget_cents ?? null,
          pm_project_id: input.pm_project_id ?? null,
        } as never)
        .select(PROJECT_SELECT)
        .single();
      if (error) {
        if (isUniqueViolation(error)) throw new Error(`Project code “${code}” is already in use.`);
        throw error;
      }
      const project = data as unknown as EngProject;

      const extra = (input.memberProfileIds ?? []).filter(
        (pid) => pid !== profile.id && pid !== (input.owner_id ?? profile.id),
      );
      if (extra.length > 0) {
        const { error: memberErr } = await supabase.from('eng_project_members' as never).insert(
          extra.map((pid) => ({
            project_id: project.id,
            profile_id: pid,
            organization_id: profile.organization_id,
            role: 'engineer',
            added_by: profile.id,
          })) as never,
        );
        if (memberErr) throw memberErr;
      }
      return project;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: engKeys.all });
    },
  });
}

export type EngProjectPatch = Partial<
  Pick<
    EngProject,
    | 'code'
    | 'name'
    | 'description'
    | 'status'
    | 'owner_id'
    | 'pm_project_id'
    | 'start_date'
    | 'target_date'
    | 'budget_cents'
    | 'image_path'
    | 'archived_at'
  >
>;

export function useUpdateEngProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: EngProjectPatch }) => {
      const patch: Record<string, unknown> = { ...input.patch };
      for (const key of ['description', 'start_date', 'target_date'] as const) {
        if (key in patch && typeof patch[key] === 'string') patch[key] = blankToNull(patch[key] as string);
      }
      if (typeof patch.code === 'string') {
        patch.code = patch.code.trim();
        if (!patch.code) throw new Error('Project code can’t be empty.');
      }
      if (typeof patch.name === 'string') {
        patch.name = patch.name.trim();
        if (!patch.name) throw new Error('Project name can’t be empty.');
      }
      const { data, error } = await supabase
        .from('eng_projects' as never)
        .update(patch as never)
        .eq('id', input.id)
        .select(PROJECT_SELECT)
        .single();
      if (error) {
        if (isUniqueViolation(error)) throw new Error('That project code is already in use.');
        throw error;
      }
      return data as unknown as EngProject;
    },
    onSuccess: (row) => {
      qc.setQueryData(engKeys.project(row.id), {
        ...row,
        eng_project_members: row.eng_project_members ?? [],
      });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
      qc.invalidateQueries({ queryKey: engKeys.history(row.id) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'item'] });
    },
  });
}

/** Hard delete. Removes every stored object for the project afterwards, best-effort. */
export function useDeleteEngProject() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { data: versions } = await supabase
        .from('eng_file_versions' as never)
        .select('storage_path')
        .eq('project_id', input.id);
      const { data: prints } = await supabase
        .from('eng_prints' as never)
        .select('photo_path')
        .eq('project_id', input.id)
        .not('photo_path', 'is', null);
      const paths = [
        ...((versions ?? []) as unknown as Array<{ storage_path: string }>).map((v) => v.storage_path),
        ...((prints ?? []) as unknown as Array<{ photo_path: string }>).map((p) => p.photo_path),
      ];

      const { error } = await supabase.from('eng_projects' as never).delete().eq('id', input.id);
      if (error) throw error;

      // Remove in chunks — the storage API caps the batch size.
      for (let i = 0; i < paths.length; i += 100) {
        await supabase.storage.from(ENG_FILES_BUCKET).remove(paths.slice(i, i + 100));
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: engKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

export function useAddEngMembers() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { projectId: string; profileIds: string[]; role?: EngMemberRole }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (input.profileIds.length === 0) return;
      const { error } = await supabase.from('eng_project_members' as never).upsert(
        input.profileIds.map((pid) => ({
          project_id: input.projectId,
          profile_id: pid,
          organization_id: profile.organization_id,
          role: input.role ?? 'engineer',
          added_by: profile.id,
        })) as never,
        { onConflict: 'project_id,profile_id', ignoreDuplicates: true } as never,
      );
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.project(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

export function useRemoveEngMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; profileId: string }) => {
      const { error } = await supabase
        .from('eng_project_members' as never)
        .delete()
        .eq('project_id', input.projectId)
        .eq('profile_id', input.profileId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.project(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

export function useSetEngMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; profileId: string; role: EngMemberRole }) => {
      const { error } = await supabase
        .from('eng_project_members' as never)
        .update({ role: input.role } as never)
        .eq('project_id', input.projectId)
        .eq('profile_id', input.profileId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.project(vars.projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export function useEngFolders(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.folders(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_folders' as never)
        .select('*')
        .eq('project_id', projectId!)
        .order('name');
      if (error) throw error;
      return data as unknown as EngFolder[];
    },
  });
}

async function insertFolder(
  orgId: string,
  profileId: string,
  projectId: string,
  parentId: string | null,
  name: string,
): Promise<EngFolder> {
  const { data, error } = await supabase
    .from('eng_folders' as never)
    .insert({
      organization_id: orgId,
      project_id: projectId,
      parent_id: parentId,
      name: name.trim(),
      created_by: profileId,
    } as never)
    .select('*')
    .single();
  if (error) {
    if (isUniqueViolation(error)) throw new Error(`A folder named “${name.trim()}” already exists here.`);
    throw error;
  }
  return data as unknown as EngFolder;
}

export function useCreateEngFolder() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { projectId: string; parentId: string | null; name: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      if (!input.name.trim()) throw new Error('Folder name is required.');
      return insertFolder(profile.organization_id, profile.id, input.projectId, input.parentId, input.name);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.folders(vars.projectId) });
    },
  });
}

export function useUpdateEngFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      projectId: string;
      patch: { name?: string; parent_id?: string | null };
    }) => {
      const patch: Record<string, unknown> = { ...input.patch };
      if (typeof patch.name === 'string') {
        patch.name = patch.name.trim();
        if (!patch.name) throw new Error('Folder name is required.');
      }
      const { error } = await supabase
        .from('eng_folders' as never)
        .update(patch as never)
        .eq('id', input.id);
      if (error) {
        if (isUniqueViolation(error)) throw new Error('A folder with that name already exists here.');
        throw error;
      }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.folders(vars.projectId) });
    },
  });
}

/** Delete a folder; its sub-folders and files move up one level. */
export function useDeleteEngFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { folder: EngFolder }) => {
      const { folder } = input;
      const { error: subErr } = await supabase
        .from('eng_folders' as never)
        .update({ parent_id: folder.parent_id } as never)
        .eq('parent_id', folder.id);
      if (subErr) throw subErr;
      const { error: fileErr } = await supabase
        .from('eng_files' as never)
        .update({ folder_id: folder.parent_id } as never)
        .eq('folder_id', folder.id);
      if (fileErr) throw fileErr;
      const { error } = await supabase.from('eng_folders' as never).delete().eq('id', folder.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.folders(vars.folder.project_id) });
      qc.invalidateQueries({ queryKey: engKeys.files(vars.folder.project_id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Files + versions
// ---------------------------------------------------------------------------

export function useEngFiles(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.files(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_files' as never)
        .select(FILE_SELECT)
        .eq('project_id', projectId!)
        .order('name');
      if (error) throw error;
      return data as unknown as EngFile[];
    },
  });
}

export function useEngFileVersions(fileId: string | undefined) {
  return useQuery({
    queryKey: engKeys.versions(fileId ?? 'noop'),
    enabled: !!fileId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_file_versions' as never)
        .select(VERSION_SELECT)
        .eq('file_id', fileId!)
        .order('version_no', { ascending: false });
      if (error) throw error;
      return data as unknown as EngFileVersion[];
    },
  });
}

async function uploadObject(path: string, file: File) {
  const { error } = await supabase.storage.from(ENG_FILES_BUCKET).upload(path, file, {
    upsert: false,
    cacheControl: '3600',
    contentType: file.type || 'application/octet-stream',
  });
  if (error) throw error;
}

async function insertVersion(input: {
  orgId: string;
  projectId: string;
  fileId: string;
  path: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  changeSummary: string | null;
  uploadedBy: string;
}): Promise<EngFileVersion> {
  const { data, error } = await supabase
    .from('eng_file_versions' as never)
    .insert({
      organization_id: input.orgId,
      project_id: input.projectId,
      file_id: input.fileId,
      storage_path: input.path,
      file_name: input.fileName,
      mime_type: input.mimeType,
      size_bytes: input.sizeBytes,
      change_summary: input.changeSummary,
      uploaded_by: input.uploadedBy,
    } as never)
    .select(VERSION_SELECT)
    .single();
  if (error) throw error;
  return data as unknown as EngFileVersion;
}

/** Create a brand-new file row plus its first version. */
async function createFileWithVersion(input: {
  orgId: string;
  profileId: string;
  projectId: string;
  folderId: string | null;
  file: File;
  changeSummary: string | null;
  itemId?: string | null;
}): Promise<EngFile> {
  const { data: created, error: fileErr } = await supabase
    .from('eng_files' as never)
    .insert({
      organization_id: input.orgId,
      project_id: input.projectId,
      folder_id: input.folderId,
      name: input.file.name,
      category: categoryForFileName(input.file.name),
      item_id: input.itemId ?? null,
      created_by: input.profileId,
    } as never)
    .select('id')
    .single();
  if (fileErr) {
    if (isUniqueViolation(fileErr)) {
      throw new Error(`“${input.file.name}” already exists in this folder.`);
    }
    throw fileErr;
  }
  const fileId = (created as unknown as { id: string }).id;
  const path = versionPath(input.orgId, input.projectId, fileId, input.file.name);
  try {
    await uploadObject(path, input.file);
  } catch (err) {
    await supabase.from('eng_files' as never).delete().eq('id', fileId);
    throw err;
  }
  try {
    await insertVersion({
      orgId: input.orgId,
      projectId: input.projectId,
      fileId,
      path,
      fileName: input.file.name,
      mimeType: input.file.type || null,
      sizeBytes: input.file.size,
      changeSummary: input.changeSummary,
      uploadedBy: input.profileId,
    });
  } catch (err) {
    await supabase.storage.from(ENG_FILES_BUCKET).remove([path]);
    await supabase.from('eng_files' as never).delete().eq('id', fileId);
    throw err;
  }
  const { data: row, error: readErr } = await supabase
    .from('eng_files' as never)
    .select(FILE_SELECT)
    .eq('id', fileId)
    .single();
  if (readErr) throw readErr;
  return row as unknown as EngFile;
}

/** Add a new version to an existing file. The trigger numbers it and makes it current. */
async function addVersionToFile(input: {
  orgId: string;
  profileId: string;
  file: Pick<EngFile, 'id' | 'project_id'>;
  upload: File;
  changeSummary: string | null;
}): Promise<EngFileVersion> {
  const path = versionPath(input.orgId, input.file.project_id, input.file.id, input.upload.name);
  await uploadObject(path, input.upload);
  try {
    return await insertVersion({
      orgId: input.orgId,
      projectId: input.file.project_id,
      fileId: input.file.id,
      path,
      fileName: input.upload.name,
      mimeType: input.upload.type || null,
      sizeBytes: input.upload.size,
      changeSummary: input.changeSummary,
      uploadedBy: input.profileId,
    });
  } catch (err) {
    await supabase.storage.from(ENG_FILES_BUCKET).remove([path]);
    throw err;
  }
}

export interface UploadEngFilesInput {
  projectId: string;
  /** Folder the batch lands in (null = drive root). */
  folderId: string | null;
  /** Each entry carries the directory segments below `folderId` (from a folder pick / drop). */
  entries: Array<{ file: File; dirs: string[] }>;
  /** Files already in the project, used to turn same-name uploads into new versions. */
  existingFiles: EngFile[];
  existingFolders: EngFolder[];
  changeSummary?: string | null;
  onProgress?: (done: number, total: number) => void;
}

export interface UploadEngFilesResult {
  created: number;
  versioned: number;
  failed: Array<{ name: string; error: string }>;
}

/**
 * Upload a batch of loose files and/or folder picks. A file whose name
 * matches an existing file in the destination folder (case-insensitive)
 * becomes that file's next version instead of a duplicate; folder
 * segments that don't exist yet are created on the way down.
 */
export function useUploadEngFiles() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: UploadEngFilesInput): Promise<UploadEngFilesResult> => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const orgId = profile.organization_id;
      const result: UploadEngFilesResult = { created: 0, versioned: 0, failed: [] };

      // Folder lookup: "<parent>/<lower name>" → id, seeded from the live list
      // and grown as the batch creates folders.
      const folderKey = (parentId: string | null, name: string) => `${parentId ?? 'root'}/${name.toLowerCase()}`;
      const folderIds = new Map<string, string>();
      for (const f of input.existingFolders) folderIds.set(folderKey(f.parent_id, f.name), f.id);

      const ensureFolder = async (parentId: string | null, dirs: string[]): Promise<string | null> => {
        let current = parentId;
        for (const dir of dirs) {
          const key = folderKey(current, dir);
          let id = folderIds.get(key);
          if (!id) {
            const folder = await insertFolder(orgId, profile.id, input.projectId, current, dir);
            id = folder.id;
            folderIds.set(key, id);
          }
          current = id;
        }
        return current;
      };

      // Same-name detection scoped to the destination folder.
      const fileKey = (folderId: string | null, name: string) => `${folderId ?? 'root'}/${name.toLowerCase()}`;
      const existing = new Map<string, EngFile>();
      for (const f of input.existingFiles) existing.set(fileKey(f.folder_id, f.name), f);

      let done = 0;
      for (const entry of input.entries) {
        input.onProgress?.(done, input.entries.length);
        done += 1;
        try {
          const folderId = await ensureFolder(input.folderId, entry.dirs);
          const match = existing.get(fileKey(folderId, entry.file.name));
          if (match) {
            await addVersionToFile({
              orgId,
              profileId: profile.id,
              file: match,
              upload: entry.file,
              changeSummary: input.changeSummary ?? null,
            });
            result.versioned += 1;
          } else {
            const created = await createFileWithVersion({
              orgId,
              profileId: profile.id,
              projectId: input.projectId,
              folderId,
              file: entry.file,
              changeSummary: input.changeSummary ?? null,
            });
            existing.set(fileKey(folderId, created.name), created);
            result.created += 1;
          }
        } catch (err) {
          result.failed.push({
            name: entry.file.name,
            error: err instanceof Error ? err.message : 'Upload failed',
          });
        }
      }
      input.onProgress?.(done, input.entries.length);
      return result;
    },
    onSettled: (_d, _e, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.projectId) });
      qc.invalidateQueries({ queryKey: engKeys.folders(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

/** Explicit "upload new version" from a file's detail panel. */
export function useUploadEngFileVersion() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { file: EngFile; upload: File; changeSummary: string | null }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      return addVersionToFile({
        orgId: profile.organization_id,
        profileId: profile.id,
        file: input.file,
        upload: input.upload,
        changeSummary: blankToNull(input.changeSummary),
      });
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.file.project_id) });
      qc.invalidateQueries({ queryKey: engKeys.versions(vars.file.id) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'item'] });
    },
  });
}

/** Restore an older version by copying it forward as the newest version. */
export function useRestoreEngFileVersion() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { file: EngFile; version: EngFileVersion }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = versionPath(
        profile.organization_id,
        input.file.project_id,
        input.file.id,
        input.version.file_name,
      );
      const { error: copyErr } = await supabase.storage
        .from(ENG_FILES_BUCKET)
        .copy(input.version.storage_path, path);
      if (copyErr) throw copyErr;
      try {
        return await insertVersion({
          orgId: profile.organization_id,
          projectId: input.file.project_id,
          fileId: input.file.id,
          path,
          fileName: input.version.file_name,
          mimeType: input.version.mime_type,
          sizeBytes: input.version.size_bytes,
          changeSummary: `Restored from v${input.version.version_no}`,
          uploadedBy: profile.id,
        });
      } catch (err) {
        await supabase.storage.from(ENG_FILES_BUCKET).remove([path]);
        throw err;
      }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.file.project_id) });
      qc.invalidateQueries({ queryKey: engKeys.versions(vars.file.id) });
    },
  });
}

export type EngFilePatch = Partial<
  Pick<EngFile, 'name' | 'description' | 'folder_id' | 'item_id' | 'checked_out_by'>
>;

export function useUpdateEngFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string; patch: EngFilePatch }) => {
      const patch: Record<string, unknown> = { ...input.patch };
      if (typeof patch.name === 'string') {
        patch.name = patch.name.trim();
        if (!patch.name) throw new Error('File name is required.');
      }
      if ('description' in patch && typeof patch.description === 'string') {
        patch.description = blankToNull(patch.description);
      }
      const { data, error } = await supabase
        .from('eng_files' as never)
        .update(patch as never)
        .eq('id', input.id)
        .select(FILE_SELECT)
        .single();
      if (error) {
        if (isUniqueViolation(error)) throw new Error('A file with that name already exists in that folder.');
        throw error;
      }
      return data as unknown as EngFile;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'item'] });
    },
  });
}

/**
 * "Download to edit": signed download of the current version plus the
 * soft check-out so teammates see who has it. Pass `checkOut: false` for
 * a plain download.
 */
export function useDownloadEngFile() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { file: EngFile; version?: EngFileVersion | null; checkOut: boolean }) => {
      const version = input.version ?? input.file.current_version;
      if (!version) throw new Error('This file has no uploaded version yet.');
      const url = await createEngSignedUrl(version.storage_path, version.file_name);
      triggerBrowserDownload(url, version.file_name);
      if (input.checkOut && profile) {
        const { error } = await supabase
          .from('eng_files' as never)
          .update({ checked_out_by: profile.id, checked_out_at: new Date().toISOString() } as never)
          .eq('id', input.file.id);
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      if (vars.checkOut) qc.invalidateQueries({ queryKey: engKeys.files(vars.file.project_id) });
    },
  });
}

export function useReleaseEngCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: EngFile }) => {
      const { error } = await supabase
        .from('eng_files' as never)
        .update({ checked_out_by: null, checked_out_at: null } as never)
        .eq('id', input.file.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.file.project_id) });
    },
  });
}

export function useDeleteEngFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: EngFile }) => {
      const { data: versions } = await supabase
        .from('eng_file_versions' as never)
        .select('storage_path')
        .eq('file_id', input.file.id);
      const paths = ((versions ?? []) as unknown as Array<{ storage_path: string }>).map(
        (v) => v.storage_path,
      );
      const { error } = await supabase.from('eng_files' as never).delete().eq('id', input.file.id);
      if (error) throw error;
      if (paths.length > 0) await supabase.storage.from(ENG_FILES_BUCKET).remove(paths);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.files(vars.file.project_id) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'item'] });
    },
  });
}

/** Ten-minute signed URL for inline previews (images, 3D viewer). */
export function useEngSignedUrl(path: string | null | undefined, download?: string | null) {
  return useQuery({
    queryKey: engKeys.signedUrl(path ?? '', download ?? null),
    enabled: !!path,
    staleTime: 8 * 60 * 1000,
    queryFn: async () => (path ? createEngSignedUrl(path, download) : null),
  });
}

// ---------------------------------------------------------------------------
// 3D prints
// ---------------------------------------------------------------------------

/** Prints for one project, or every print the caller can see when `projectId` is null. */
export function useEngPrints(projectId: string | null | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: engKeys.prints(projectId ?? null),
    enabled: !!profile && projectId !== undefined,
    queryFn: async () => {
      let q = supabase
        .from('eng_prints' as never)
        .select(PRINT_SELECT)
        .order('printed_on', { ascending: false, nullsFirst: true })
        .order('created_at', { ascending: false });
      if (projectId) q = q.eq('project_id', projectId);
      const { data, error } = await q;
      if (error) throw error;
      return (data as unknown as EngPrint[]).map(normalizePrint);
    },
  });
}

export interface EngPrintValues {
  title: string;
  file_id: string | null;
  file_version_id: string | null;
  equipment_id: string | null;
  printer_name: string | null;
  material: string | null;
  color: string | null;
  quantity: number;
  layer_height_mm: number | null;
  infill_percent: number | null;
  print_time_minutes: number | null;
  material_grams: number | null;
  cost_cents: number | null;
  status: EngPrintStatus;
  printed_on: string | null;
  printed_by: string | null;
  notes: string | null;
  changes_needed: string | null;
}

export function useSaveEngPrint() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { id?: string; projectId: string; values: EngPrintValues }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const values = {
        ...input.values,
        title: input.values.title.trim(),
        printer_name: blankToNull(input.values.printer_name),
        material: blankToNull(input.values.material),
        color: blankToNull(input.values.color),
        notes: blankToNull(input.values.notes),
        changes_needed: blankToNull(input.values.changes_needed),
        printed_on: input.values.printed_on || null,
      };
      if (!values.title) throw new Error('Give the print a name (usually the part).');
      if (!Number.isFinite(values.quantity) || values.quantity < 1) {
        throw new Error('Quantity must be at least 1.');
      }
      const query = input.id
        ? supabase
            .from('eng_prints' as never)
            .update(values as never)
            .eq('id', input.id)
            .select(PRINT_SELECT)
            .single()
        : supabase
            .from('eng_prints' as never)
            .insert({
              ...values,
              organization_id: profile.organization_id,
              project_id: input.projectId,
              created_by: profile.id,
            } as never)
            .select(PRINT_SELECT)
            .single();
      const { data, error } = await query;
      if (error) throw error;
      return normalizePrint(data as unknown as EngPrint);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'prints'] });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
      qc.invalidateQueries({ queryKey: engKeys.costs(vars.projectId) });
    },
  });
}

export function useDeleteEngPrint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { print: EngPrint }) => {
      const { error } = await supabase.from('eng_prints' as never).delete().eq('id', input.print.id);
      if (error) throw error;
      if (input.print.photo_path) {
        await supabase.storage.from(ENG_FILES_BUCKET).remove([input.print.photo_path]);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'prints'] });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

export function useUploadEngPrintPhoto() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { print: EngPrint; file: File }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = printPhotoPath(
        profile.organization_id,
        input.print.project_id,
        input.print.id,
        input.file.name,
      );
      await uploadObject(path, input.file);
      const { error } = await supabase
        .from('eng_prints' as never)
        .update({ photo_path: path } as never)
        .eq('id', input.print.id);
      if (error) {
        await supabase.storage.from(ENG_FILES_BUCKET).remove([path]);
        throw error;
      }
      if (input.print.photo_path) {
        await supabase.storage.from(ENG_FILES_BUCKET).remove([input.print.photo_path]);
      }
      return path;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'prints'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Costs
// ---------------------------------------------------------------------------

export function useEngCosts(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.costs(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_costs' as never)
        .select(COST_SELECT)
        .eq('project_id', projectId!)
        .order('incurred_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as EngCost[]).map(normalizeCost);
    },
  });
}

export interface EngCostValues {
  description: string;
  category: EngCostCategory;
  vendor: string | null;
  reference: string | null;
  item_id: string | null;
  print_id: string | null;
  quantity: number | null;
  unit_cents: number | null;
  amount_cents: number;
  incurred_on: string;
}

export function useSaveEngCost() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { id?: string; projectId: string; values: EngCostValues }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const values = {
        ...input.values,
        description: input.values.description.trim(),
        vendor: blankToNull(input.values.vendor),
        reference: blankToNull(input.values.reference),
      };
      if (!values.description) throw new Error('Describe what the cost was for.');
      if (!Number.isFinite(values.amount_cents) || values.amount_cents < 0) {
        throw new Error('Enter a valid amount.');
      }
      const query = input.id
        ? supabase
            .from('eng_costs' as never)
            .update(values as never)
            .eq('id', input.id)
        : supabase.from('eng_costs' as never).insert({
            ...values,
            organization_id: profile.organization_id,
            project_id: input.projectId,
            created_by: profile.id,
          } as never);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.costs(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

export function useDeleteEngCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string }) => {
      const { error } = await supabase.from('eng_costs' as never).delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.costs(vars.projectId) });
      qc.invalidateQueries({ queryKey: [...engKeys.all, 'projects'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Item links (production tie-in)
// ---------------------------------------------------------------------------

export function useEngProjectItems(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.items(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_project_items' as never)
        .select(PROJECT_ITEM_SELECT)
        .eq('project_id', projectId!)
        .order('created_at');
      if (error) throw error;
      return data as unknown as EngProjectItem[];
    },
  });
}

/** Engineering projects + files linked to one catalog item (for the item detail page). */
export function useEngLinksForItem(itemId: string | undefined) {
  const { profile } = useAuth();
  return useQuery({
    queryKey: engKeys.itemLinks(itemId ?? 'noop'),
    enabled: !!profile && !!itemId,
    queryFn: async () => {
      const [links, files] = await Promise.all([
        supabase
          .from('eng_project_items' as never)
          .select(ITEM_LINK_SELECT)
          .eq('item_id', itemId!)
          .order('created_at'),
        supabase
          .from('eng_files' as never)
          .select(`${FILE_SELECT}, project:eng_projects!eng_files_project_id_fkey(id, code, name, status)`)
          .eq('item_id', itemId!)
          .order('name'),
      ]);
      if (links.error) throw links.error;
      if (files.error) throw files.error;
      return {
        projects: (links.data ?? []) as unknown as EngProjectLinkForItem[],
        files: (files.data ?? []) as unknown as Array<
          EngFile & { project: { id: string; code: string; name: string; status: EngStatus } | null }
        >,
      };
    },
  });
}

export function useLinkEngProjectItem() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      projectId: string;
      itemId: string;
      relationship: EngItemRelationship;
      note?: string | null;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { error } = await supabase.from('eng_project_items' as never).upsert(
        {
          project_id: input.projectId,
          item_id: input.itemId,
          organization_id: profile.organization_id,
          relationship: input.relationship,
          note: blankToNull(input.note),
          added_by: profile.id,
        } as never,
        { onConflict: 'project_id,item_id' } as never,
      );
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.items(vars.projectId) });
      qc.invalidateQueries({ queryKey: engKeys.itemLinks(vars.itemId) });
    },
  });
}

export function useUnlinkEngProjectItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { projectId: string; itemId: string }) => {
      const { error } = await supabase
        .from('eng_project_items' as never)
        .delete()
        .eq('project_id', input.projectId)
        .eq('item_id', input.itemId);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.items(vars.projectId) });
      qc.invalidateQueries({ queryKey: engKeys.itemLinks(vars.itemId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export function useEngNotes(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.notes(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_notes' as never)
        .select(NOTE_SELECT)
        .eq('project_id', projectId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as EngNote[];
    },
  });
}

export function useCreateEngNote() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { projectId: string; body: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const body = input.body.trim();
      if (!body) throw new Error('Write something first.');
      const { error } = await supabase.from('eng_notes' as never).insert({
        organization_id: profile.organization_id,
        project_id: input.projectId,
        author_id: profile.id,
        body,
      } as never);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.notes(vars.projectId) });
    },
  });
}

export function useDeleteEngNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; projectId: string }) => {
      const { error } = await supabase.from('eng_notes' as never).delete().eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: engKeys.notes(vars.projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Status history
// ---------------------------------------------------------------------------

export function useEngStatusHistory(projectId: string | undefined) {
  return useQuery({
    queryKey: engKeys.history(projectId ?? 'noop'),
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eng_status_history' as never)
        .select(HISTORY_SELECT)
        .eq('project_id', projectId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as EngStatusHistoryRow[];
    },
  });
}
